<template>
	<div class="flex flex-col w-dvw min-h-(--min-height-custom-mobile) lg:min-h-(--min-height-custom) items-center gap-8 lg:gap-16">
		<h1 class="flex flex-col text-5xl md:text-7xl w-full justify-center text-center pt-8">
			Dress Code
		</h1>
		<Divider class="w-32! -my-4 lg:-my-8!" />
		<div class="flex flex-col w-4/5 gap-4">
			<h1 class="text-2xl md:text-3xl uppercase text-center">
				Black Tie Optional
			</h1>
			<div class="flex flex-col md:flex-row flex-wrap justify-center items-center gap-x-2 gap-y-1 text-base md:text-lg lg:text-xl text-center">
				<span>Floor Length Gowns</span>
				<span class="hidden md:block">&bull;</span>
				<Divider class="w-32! md:hidden!" />
				<span>Dressy Jumpsuits</span>
				<span class="hidden md:block">&bull;</span>
				<Divider class="w-32! md:hidden!" />
				<span>Pantsuits</span>
				<span class="hidden md:block">&bull;</span>
				<Divider class="w-32! md:hidden!" />
				<span>Tuxedos</span>
				<span class="hidden md:block">&bull;</span>
				<Divider class="w-32! md:hidden!" />
				<span>Dark Colored Suit and Tie</span>
				<Divider class="w-32! md:hidden!" />
			</div>
			<p class="text-base md:text-lg lg:text-xl text-center -mt-4 md:m-0">
				Please refrain from wearing the colors below as they are reserved for the wedding party
			</p>
			<!-- Primary Colors -->
			<div class="flex w-full relative">
				<!-- Primary Color Container -->
				<div class="flex w-full justify-center gap-12">
					<!-- Color One -->
					<div class="flex flex-col items-center gap-2">
						<span class="flex w-full justify-center font-extralight!">Olive</span>
						<span class="flex size-20 rounded-full shadow-lg border border-black bg-(--primary-olive)" />
					</div>

					<!-- Color Two -->
					<div class="flex flex-col items-center gap-2">
						<span class="flex w-full justify-center font-extralight!">Forest</span>
						<span class="flex size-20 rounded-full shadow-lg border border-black bg-(--secondary-olive)" />
					</div>

					<!-- Color Three -->
					<div class="flex flex-col items-center gap-2">
						<span class="flex w-full justify-center font-extralight!">Dusty Rose</span>
						<span class="flex size-20 rounded-full shadow-lg border border-black bg-(--accent-maroon)" />
					</div>
				</div>
			</div>

			<!-- Secondary Colors -->
			<div class="flex w-full relative">
				<!-- Secondary Color Container -->
				<div class="flex w-full justify-center gap-12">
					<!-- Color Four -->
					<div class="flex flex-col items-center gap-2">
						<span class="flex size-20 rounded-full shadow-lg border border-black bg-(--primary-beige)" />
						<span class="flex w-full justify-center font-extralight!">Beige</span>
					</div>

					<!-- Color Five -->
					<div class="flex flex-col items-center gap-2">
						<span class="flex size-20 rounded-full shadow-lg border border-black bg-white" />
						<span class="flex w-full justify-center font-extralight!">White</span>
					</div>

					<!-- Color Six -->
					<div class="flex flex-col items-center gap-2">
						<span class="flex size-20 rounded-full shadow-lg border border-black bg-(--primary-brown)" />
						<span class="flex w-full justify-center font-extralight!">Brown</span>
					</div>
				</div>
			</div>
		</div>
		<div class="flex flex-col size-full items-center gap-4">
			<h1 class="flex text-4xl md:text-7xl w-full justify-center text-center uppercase mt-8">
				Dress Inspiration
			</h1>
			<div class="grid grid-cols-[repeat(auto-fit,160px)] md:auto-rows-2.5 justify-center w-4/5 gap-4">
				<!-- Grid of images displayed as thumbnails -->
				<div
					v-for="photo in inspirationPhotos.dress"
					:key="photo.index"
					class="grid-item flex justify-center max-w-40 lg:max-w-80 overflow-scroll"
				>
					<div
						v-if="!contentLoaded"
						class="size-full"
					>
						<LoadingCard
							:show-loading-text="false"
							height="full"
						/>
					</div>
					<div v-else>
						<!-- Displays each image thumbnail using Nuxt's ImageKit provider -->
						<NuxtImg
							v-if="ALLOW_API_CALLS"
							class="object-cover max-h-(--max-img-height) rounded-lg"
							provider="imagekit"
							:src="photo.src"
							:alt="photo.alt"
							:loading="photo.index < EAGER_LOAD_COUNT ? 'eager' : 'lazy'"
							@load="onImageLoad"
						/>
						<img
							v-else
							class="object-contain max-h-(--max-img-height) rounded-lg"
							:src="photo.src"
							:alt="photo.alt"
							:loading="photo.index < EAGER_LOAD_COUNT ? 'eager' : 'lazy'"
							@load="onImageLoad"
						>
					</div>
				</div>
			</div>
		</div>
		<div class="flex flex-col size-full items-center gap-4">
			<h1 class="flex text-4xl md:text-7xl w-full justify-center text-center uppercase mt-8">
				Suit Inspiration
			</h1>
			<div class="grid grid-cols-[repeat(auto-fit,160px)] md:auto-rows-2.5 justify-center w-4/5 gap-4">
				<!-- Grid of images displayed as thumbnails -->
				<div
					v-for="photo in inspirationPhotos.suit"
					:key="photo.index"
					class="grid-item justify-center flex max-w-80 overflow-scroll hover:cursor-pointer"
				>
					<div
						v-if="!contentLoaded"
						class="size-full"
					>
						<LoadingCard
							:show-loading-text="false"
							height="full"
						/>
					</div>
					<div v-else>
						<!-- Displays each image thumbnail using Nuxt's ImageKit provider -->
						<NuxtImg
							v-if="ALLOW_API_CALLS"
							class="object-cover max-h-(--max-img-height) rounded-lg"
							provider="imagekit"
							:src="photo.src"
							:alt="photo.alt"
							@load="onImageLoad"
						/>
						<img
							v-else
							class="object-contain max-h-(--max-img-height) rounded-lg"
							:src="photo.src"
							:alt="photo.alt"
							@load="onImageLoad"
						>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import LoadingCard from "~/components/LoadingCard.vue";
	import {
		Divider
	} from "primevue";

	const { contentLoaded, inspirationPhotos } = imageDataStores();
	const { ALLOW_API_CALLS } = globalStores();

	const ROW_HEIGHT = 10; // same as auto-rows
	const GAP = 16; // same as Tailwind gap-4 in px
	const EAGER_LOAD_COUNT = 16;

	/**
	 * Sets the grid-row-end span of a single grid item based on the
	 * current rendered height of its image.
	 *
	 * @param {HTMLElement} item - The `.grid-item` element to size.
	 */
	const setGridItemSpan = ( item: HTMLElement ) => {
		const img = item.querySelector<HTMLImageElement>( "img" );
		if ( !img ) return;
		const totalHeight = img.getBoundingClientRect().height + GAP;
		const rowSpan = Math.ceil( totalHeight / ( ROW_HEIGHT + GAP ) );
		item.style.gridRowEnd = `span ${ rowSpan }`;
	};

	const setGridItemSpans = ( container: HTMLElement ) => {
		const items = container.querySelectorAll<HTMLElement>( ".grid-item" );
		items.forEach( setGridItemSpan );
	};

	let resizeObserver: ResizeObserver | null = null;

	/**
	 * (Re)observes every thumbnail image in the grid so that spans get
	 * recalculated whenever an image's rendered size changes - this
	 * catches layout shifts (e.g. responsive breakpoints) that a one-off
	 * `load` event wouldn't.
	 *
	 * @param {HTMLElement} container - The grid container element.
	 */
	const observeGridImages = ( container: HTMLElement ) => {
		resizeObserver?.disconnect();
		resizeObserver = new ResizeObserver( ( entries ) => {
			entries.forEach( ( entry ) => {
				const item = ( entry.target as HTMLElement ).closest<HTMLElement>( ".grid-item" );
				if ( item ) setGridItemSpan( item );
			} );
		} );
		container.querySelectorAll<HTMLImageElement>( ".grid-item img" ).forEach( ( img ) => {
			resizeObserver?.observe( img );
		} );
	};

	/**
	 * Recalculates the span of the grid item belonging to whichever
	 * image just finished loading, since images from ImageKit resolve
	 * at different times and the initial layout pass can't wait on all
	 * of them.
	 *
	 * @param {Event} event - The native `load` event from the `<img>`.
	 */
	const onImageLoad = ( event: Event ) => {
		const img = event.target as HTMLImageElement;
		const item = img.closest<HTMLElement>( ".grid-item" );
		if ( item ) setGridItemSpan( item );
	};

	const applyGridItemSpans = async () => {
		await nextTick();
		const container = document.querySelector<HTMLElement>( ".grid" );
		if ( container ) {
			setGridItemSpans( container );
			observeGridImages( container );
		}
	};

	watch( inspirationPhotos, applyGridItemSpans );

	onBeforeUnmount( () => {
		resizeObserver?.disconnect();
	} );

	onMounted( () => {
		applyGridItemSpans();
	} );
</script>

<style scoped>

</style>